/*
 * The one decision behind keeping a built grid in step with its panel list: which items
 * leave, which arrive, which only change their bar's title. Pure, so it can be proven
 * without a DOM — the grid itself is imperative gridstack, and this is the seam where a
 * removed tile once kept its empty frame and an installed one waited for a full rebuild.
 */

/** What the grid already holds: id → the title its bar shows. */
export type BuiltItems = ReadonlyMap<string, string>;

export interface PanelDiff<P extends { id: string; title?: string }> {
  /** Built items whose panel is gone, in the grid's own order. */
  remove: string[];
  /** Panels with no item yet, in the list's order. */
  add: P[];
  /** Kept items whose bar no longer says what the panel does. */
  retitle: { id: string; title: string }[];
}

export function diffPanels<P extends { id: string; title?: string }>(built: BuiltItems, want: readonly P[]): PanelDiff<P> {
  const wanted = new Map(want.map((p) => [p.id, p]));
  const remove: string[] = [];
  for (const id of built.keys()) if (!wanted.has(id)) remove.push(id);
  const add: P[] = [];
  const retitle: { id: string; title: string }[] = [];
  for (const p of want) {
    const shown = built.get(p.id);
    if (shown === undefined) add.push(p);
    else if (shown !== (p.title ?? '')) retitle.push({ id: p.id, title: p.title ?? '' });
  }
  return { remove, add, retitle };
}
